import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';

type OnboardingStep =
    | 'dependency-setup'
    | 'locations'
    | 'it-setup'
    | 'mitchell-logins'
    | 'ops-logins'
    | 'supplier-network'
    | 'invite-users'
    | 'org-settings';

interface OnboardingStepResponse {
    onboardingStep: OnboardingStep | null;
    organizationId: string;
    organizationName: string;
    locations: Array<{ _id: string; title: string }>;
}

export const useGetOnboardingStep = () => {
    const session = useContext(SessionContext);
    const sessionId = session?.sessionId;

    return useQuery<OnboardingStepResponse>({
        queryKey: ['onboarding-step', sessionId],
        queryFn: async () => {
            const { data } = await axios.get(`${API_URL}/organization/onboarding-step`, {
                headers: { Authorization: sessionId! },
            });
            return data;
        },
        enabled: !!sessionId,
        staleTime: 30 * 1000,
    });
};

export const useSaveOnboardingStep = () => {
    const session = useContext(SessionContext);
    const sessionId = session?.sessionId;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (step: OnboardingStep | null) => {
            await axios.post(
                `${API_URL}/organization/onboarding-step`,
                { step },
                { headers: { Authorization: sessionId! } }
            );
        },
        onSuccess: () => {
            // Invalidate the onboarding-step query so the cache stays in sync
            queryClient.invalidateQueries({ queryKey: ['onboarding-step', sessionId] });
        },
    });
};
